
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import SweepSearch from '../SweepSearch';
import useMinMax from '../Hooks/MinMax';

import axios from 'axios';
import { SweepBackend } from '../../utils/Backend';
import { sweepResourses } from '../../helper/constants';
import { 
  usedSortOptions, fuelTypeOptions,
  minPriceRange, maxPriceRange, 
  minYearsRange, maxYearsRange, 
  minMileagesRange, maxMileagesRange,
} from '../../helper/filters';
import { processArrayFilter } from '../../helper/vehicle_url';
import { toast } from 'react-toastify';

import { vehicleFilterEvent } from '../../utils/Analytics';

import IOption from "../../interfaces/IOption";
import iFilters from '../../interfaces/iFilters';
import { useIsBoiWebsite } from "../Context/WebsiteType";

export default function SweepFiltersManager(
  filters: iFilters, 
  setFilters: (filters: iFilters) => void,
  onlyMakesAndModels = false, 
  setRefresh?: (refresh: boolean) => void,
  cacheStatus?: string, 
  updateURL?: boolean, 
  setUpdateURL?: (updateURL: boolean) => void, 
  tablet?: boolean, 
) {
  const [makes, setMakes] = useState<IOption[]>([]);
  const [models, setModels] = useState<IOption[]>([]);
  const [bodyTypes, setBodyTypes] = useState<IOption[]>([]);
  const [colors, setColors] = useState<IOption[]>([]);
  const [locationStates, setLocationStates] = useState<IOption[]>([]);

  const [minPrices, maxPrices] = useMinMax(filters.min_price, filters.max_price, minPriceRange, maxPriceRange);
  const [minYears, maxYears] = useMinMax(filters.min_year, filters.max_year, minYearsRange, maxYearsRange);  
  const [minMileages, maxMileages] = useMinMax(filters.min_mileage, filters.max_mileage, minMileagesRange, maxMileagesRange);
  
  const [cleanModels, setCleanModels] = useState(false);

  const [processedFilters, setProcessedFilters] = useState(false)

  const [processingURL, setProcessingURL] = useState(false);
  const router = useRouter();

  const isBoiWebsite = useIsBoiWebsite();

  const filtersOptions: { [key: string]: IOption[] } = useMemo(() => {
    return {
      order_by: usedSortOptions,
      fuel_ids: fuelTypeOptions,
      
      manufacturer_ids: makes, // fetch all makes
      model_ids: models, // fetch models based on makes
      body_ids: bodyTypes, // fetch all body types
      colors: colors, // fetch all colors
      location_states: locationStates, // fetch all states

      min_price: minPrices,
      max_price: maxPrices,

      min_year: minYears,
      max_year: maxYears,

      min_mileage: minMileages,
      max_mileage: maxMileages,
    }
  }, [makes, models, bodyTypes, colors, locationStates, minPrices, maxPrices, minYears, maxYears, minMileages, maxMileages])
  
  const processSweepData = (data: any, accessor = 'name', multi = true, sort = true) => {
    if (sort) data = data.sort((a: any, b: any) => a.key.localeCompare(b.key));
        
    const options: IOption[] = data.map((item: any) => {
      return { value: item.id, label: item[accessor]}
    })

    if (multi) {
      options.unshift({ value: 'all', label: 'All' })
    }

    return options;
  }

  // Fetch all makes
  useEffect(() => {
    const { promise, cancel } = SweepBackend.getManufacturers()

    promise.then(({data}) => {
      if (Object.keys(data)?.length !== 0) {
        setMakes(processSweepData(data))
      }
      else {
        toast.error('Makes are not available at this time.')
      }
    })
    .catch((err) => {
      if (axios.isCancel(err)) return;
      console.error('Failed to fetch makes');
    })

    return cancel;
  }, [])

  // clean up models after manufacturers change
  useEffect(() => {
    if (cleanModels) {
      setCleanModels(false)
      setModels([]);
      setFilters({
        ...filters,
        model_ids: [],
      })
    }
  }, [cleanModels, models, filters, setFilters])

  // fetching models based on makes
  useEffect(() => {
    setCleanModels(true);
    if (filters.manufacturer_ids?.length > 0) {
      const { promise, cancel } = SweepBackend.getModels(filters.manufacturer_ids)

      promise.then(({data}) => {
        if (Object.keys(data)?.length !== 0) {
          setModels(processSweepData(data))
        }
        else {
          toast.error('Models are not available at this time.')
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error('Failed to fetch models');
      })

      return cancel;
    }
  }, [filters.manufacturer_ids])

  // Fetching all Body types
  useEffect(() => {
    if (onlyMakesAndModels) return;

    const { promise, cancel } = SweepBackend.getResource(sweepResourses.bodies)

    promise.then(({data}) => {
      if (Object.keys(data)?.length !== 0) {
        setBodyTypes(processSweepData(data, 'type'))
      }
      else {
        toast.error('Body types are not available at this time.')
      }
    })
    .catch((err) => {
      if (axios.isCancel(err)) return;
      console.error('Failed to fetch body types');
    })

    return cancel;
  }, [onlyMakesAndModels])

  // Fetching all locations
  useEffect(() => {
    if (onlyMakesAndModels) return;

    const { promise, cancel } = SweepBackend.getResource(sweepResourses.locations)

    promise.then(({data}) => {
      if (Object.keys(data)?.length !== 0) {
        const options = data.map((location: string) => {
          return { value: location, label: location }
        })
        options.unshift({ value: 'all', label: 'All Counties' })
        setLocationStates(options)
      }
      else {
        toast.error('Locations are not available at this time.')
      }
    })
    .catch((err) => {
      if (axios.isCancel(err)) return;
      console.error('Failed to fetch locations');
    })

    return cancel;
  }, [onlyMakesAndModels])

  // Fetching all colors
  useEffect(() => {
    if (onlyMakesAndModels) return;

    const { promise, cancel } = SweepBackend.getResource(sweepResourses.colors)

    promise.then(({data}) => {
      if (Object.keys(data)?.length !== 0) {
        const options = data.map((color: string) => {
          return { value: color, label: color }
        })

        options.unshift({ value: 'all', label: 'All Colors' })
        setColors(options)
      }
      else {
        toast.error('Locations are not available at this time.')
      }
    })
    .catch((err) => {
      if (axios.isCancel(err)) return;
      console.error('Failed to fetch colors');
    })

    return cancel;
  }, [onlyMakesAndModels])

  // start processing URL, if cacheStatus has been defined
  useEffect(() => {
    if (onlyMakesAndModels) return;

    if (cacheStatus !== "") {
      setProcessingURL(true);
    }
  }, [cacheStatus, onlyMakesAndModels])

  const vehicleSearch = SweepSearch(filters);

  // 5 seconds debounce analytics send
  useEffect(() => {
    if (onlyMakesAndModels) return;

    const sendAnalytics = () => {
      vehicleFilterEvent(isBoiWebsite, { ...filters })
    }

    const timeout = setTimeout(sendAnalytics, 5000);
    return () => clearTimeout(timeout);
  }, [filters, onlyMakesAndModels, isBoiWebsite])

  // updating url
  useEffect(() => {
    if (onlyMakesAndModels) return;

    if (updateURL && setUpdateURL) {
      setUpdateURL(false);
      vehicleSearch();
      
      if (tablet) window.scrollTo(0, 0);
    }
  }, [updateURL, setUpdateURL, vehicleSearch, tablet, onlyMakesAndModels])

  const processFiltersURL = useCallback(() => {
    if (router.query) {
      for (let parameter in router.query) {
        if (parameter === 'slug') continue
        if (filters[parameter] === undefined) continue

        let value = `${router.query[parameter]}`

        if (Array.isArray(filters[parameter])) {
          const array = value.split(',');
          filters[parameter] = processArrayFilter(array, filtersOptions[parameter]);
        }
        else if (filters[parameter] === null) {
          const numerical_value = parseFloat(value)

          filters[parameter] = filtersOptions[parameter].find(option => {
            return option.value === numerical_value;
          });
        }
        else {
          filters[parameter] = filtersOptions[parameter].find(option => {
            return option.value === value;
          });
        }
      }

      setFilters({...filters});
    }
  }, [router, filters, setFilters, filtersOptions])

  // Processing all filters from URL
  useEffect(() => {
    if (onlyMakesAndModels) return;

    if (!processedFilters && router.isReady && processingURL) {
      // nothing to process actually.
      if (Object.keys(router.query).length === 0) {
        setProcessedFilters(true);
      }
      // waiting for all filters to be loaded
      else if (makes.length > 0 && bodyTypes.length > 0 && locationStates.length > 0) {
        processFiltersURL();
        setProcessedFilters(true);
      }
    }
  }, [bodyTypes, locationStates, makes, processFiltersURL, processedFilters, processingURL, router, setUpdateURL, onlyMakesAndModels]);

  // finish processing URL, processing models
  useEffect(() => {
    if (onlyMakesAndModels) return;

    if (processedFilters && processingURL) {
      if (filters.manufacturer_ids.length > 0) { // should've fetched models
        
        // waiting for models to be fetched
        if (models.length === 0) return; 

        // processing models
        if (router.query.model_ids) {
          const array = router.query.model_ids.toString().split(',');
          filters.model_ids = processArrayFilter(array, models);
          setFilters({...filters});
        }
      }
      
      // finish processing URL
      setProcessingURL(false);
      if (cacheStatus === "not found" && setRefresh) { // if cache not found then refresh
        setRefresh(true);
      }
      
      if (setUpdateURL) setUpdateURL(true);
    }
  }, [router, cacheStatus, setRefresh, filters, setFilters, processingURL, processedFilters, models, setUpdateURL, onlyMakesAndModels]) 

  return {
    processingURL,
    // values for selectors
    options: {
      makes, models, bodyTypes, locationStates,
      minPrices, maxPrices, minYears, maxYears,
      minMileages, maxMileages, colors
    }
  }
}